// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import GameMode from '@app/features/accessibility/state/GameMode';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const SYNC_REDUCED_MOTION_SETTING_WITH_SYSTEM_DESCRIPTOR = msg({
	message: 'Sync reduced motion setting with system',
	comment: 'Label in the motion tab.',
});
const AUTOMATICALLY_USE_YOUR_SYSTEM_S_REDUCED_MOTION_PREFERENCE_DESCRIPTOR = msg({
	message: "Use this device's system reduced motion preference, or customize it below.",
	comment: 'Description text in the motion tab.',
});
const REDUCE_MOTION_DESCRIPTOR = msg({
	message: 'Reduce motion',
	comment: 'Short label in the motion tab. Keep it concise.',
});
const DISABLE_ANIMATIONS_AND_TRANSITIONS_CURRENTLY_CONTROLLED_BY_YOUR_DESCRIPTOR = msg({
	message: 'Disable animations and transitions. Currently controlled by your system setting.',
	comment: 'Button or menu action label in the motion tab. Keep it concise.',
});
const DISABLE_ANIMATIONS_AND_TRANSITIONS_THROUGHOUT_THE_APP_DESCRIPTOR = msg({
	message: 'Disable animations and transitions throughout the app.',
	comment: 'Button or menu action label in the motion tab. Keep it concise.',
});
const CONTENT_ANIMATIONS_STAY_IN_ANIMATION_TAB_DESCRIPTOR = msg({
	message: 'Animated emojis, GIFs and stickers stay under your control in the Animation tab.',
	comment: 'Extra hint shown beneath the reduce motion toggle while reduced motion is active.',
});
const STAY_FULLY_INTERACTIVE_WHEN_UNFOCUSED_DESCRIPTOR = msg({
	message: 'Stay fully interactive when unfocused',
	comment: 'Label in the motion tab.',
});
const KEEP_ANIMATIONS_GIF_PLAYBACK_HOVER_EFFECTS_AND_TOOLTIPS_RUNNING_DESCRIPTOR = msg({
	message: 'Keep animations, GIF playback, hover effects, and tooltips running while the window is unfocused.',
	comment: 'Description text in the motion tab.',
});
const GAME_MODE_DESCRIPTOR = msg({
	message: 'Run lighter in the background',
	comment:
		'Label in the motion tab for the on-by-default setting that lightens the app while its window is not focused (apart from Performance mode, which the user turns on).',
});
const GAME_MODE_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Pauses animated images while {productName} is not focused and drops videos while it is hidden, so games keep their FPS.',
	comment: 'Description text in the motion tab. Preserve {productName}; it is inserted by code.',
});
const GAME_MODE_LIGHT_DESCRIPTOR = msg({
	message: 'Performance mode',
	comment:
		'Label in the motion tab for the Performance mode switch: the user turns it on while playing to keep the app light with its window in front too. Product feature name.',
});
const GAME_MODE_LIGHT_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Eases the load on your computer while you play: stops animations and videos that play by themselves, drops 3D rooms to 30 fps, and opens the cameras of others only when you click them. Voice chat and screen sharing are not affected. We suggest turning it off when you leave your game.',
	comment: 'Description text in the motion tab for Performance mode.',
});
const GAME_MODE_FLAT_ROOM_DESCRIPTOR = msg({
	message: 'Show 3D rooms flat',
	comment:
		'Label in the motion tab for the Performance mode option that opens 3D voice rooms as the regular call view.',
});
const GAME_MODE_FLAT_ROOM_DESCRIPTION_DESCRIPTOR = msg({
	message: 'In Performance mode, voice channels with a 3D room open as the regular call view.',
	comment: 'Description text in the motion tab for the Performance mode flat room option.',
});
export const MotionTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const syncReducedMotionWithSystem = Accessibility.syncReducedMotionWithSystem;
	const reducedMotionOverride = Accessibility.reducedMotionOverride;
	const useReducedMotion = Accessibility.useReducedMotion;
	const reduceMotionBaseDescription = syncReducedMotionWithSystem
		? i18n._(DISABLE_ANIMATIONS_AND_TRANSITIONS_CURRENTLY_CONTROLLED_BY_YOUR_DESCRIPTOR)
		: i18n._(DISABLE_ANIMATIONS_AND_TRANSITIONS_THROUGHOUT_THE_APP_DESCRIPTOR);
	const reduceMotionDescription = useReducedMotion
		? `${reduceMotionBaseDescription} ${i18n._(CONTENT_ANIMATIONS_STAY_IN_ANIMATION_TAB_DESCRIPTOR)}`
		: reduceMotionBaseDescription;
	return (
		<>
			<Switch
				label={i18n._(SYNC_REDUCED_MOTION_SETTING_WITH_SYSTEM_DESCRIPTOR)}
				description={i18n._(AUTOMATICALLY_USE_YOUR_SYSTEM_S_REDUCED_MOTION_PREFERENCE_DESCRIPTOR)}
				value={syncReducedMotionWithSystem}
				onChange={(value) => AccessibilityCommands.update({syncReducedMotionWithSystem: value})}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.update"
			/>
			<Switch
				label={i18n._(REDUCE_MOTION_DESCRIPTOR)}
				description={reduceMotionDescription}
				value={syncReducedMotionWithSystem ? useReducedMotion : (reducedMotionOverride ?? false)}
				disabled={syncReducedMotionWithSystem}
				onChange={(value) => AccessibilityCommands.update({reducedMotionOverride: value})}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.update--2"
			/>
			<Switch
				label={i18n._(STAY_FULLY_INTERACTIVE_WHEN_UNFOCUSED_DESCRIPTOR)}
				description={i18n._(KEEP_ANIMATIONS_GIF_PLAYBACK_HOVER_EFFECTS_AND_TOOLTIPS_RUNNING_DESCRIPTOR)}
				value={Accessibility.stayInteractiveWhenUnfocused}
				onChange={(value) => AccessibilityCommands.update({stayInteractiveWhenUnfocused: value})}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.update--3"
			/>
			<Switch
				label={i18n._(GAME_MODE_DESCRIPTOR)}
				description={i18n._(GAME_MODE_DESCRIPTION_DESCRIPTOR, {productName: PRODUCT_NAME})}
				value={GameMode.enabled}
				onChange={GameMode.setEnabled}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.game-mode"
			/>
			<Switch
				label={i18n._(GAME_MODE_LIGHT_DESCRIPTOR)}
				description={i18n._(GAME_MODE_LIGHT_DESCRIPTION_DESCRIPTOR)}
				value={GameMode.lightMode}
				onChange={GameMode.setLightMode}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.game-mode-light"
			/>
			<Switch
				label={i18n._(GAME_MODE_FLAT_ROOM_DESCRIPTOR)}
				description={i18n._(GAME_MODE_FLAT_ROOM_DESCRIPTION_DESCRIPTOR)}
				value={GameMode.flatRoom}
				disabled={!GameMode.lightMode}
				onChange={GameMode.setFlatRoom}
				data-flx="user.accessibility-tab.motion-tab.motion-tab-content.switch.game-mode-flat-room"
			/>
		</>
	);
});
